import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { OpenMeteoForecast, NwsAlerts } from "src/utils/weather";

// Separate RTK Query slice for third-party weather services. It deliberately
// does NOT share the main API base query, so it never attaches the Auth0
// bearer token — these are public, keyless endpoints:
//   - Open-Meteo forecast (CAPE / lightning potential included)
//   - NWS active alerts (US severe-thunderstorm / tornado warnings)
//   - RainViewer radar tile catalog (consumed by the radar map)
export const WeatherAPI = createApi({
  reducerPath: "weatherApi",
  baseQuery: fetchBaseQuery({ baseUrl: "" }),
  endpoints: (builder) => ({
    getForecast: builder.query<OpenMeteoForecast, { lat: number; lon: number }>({
      query: ({ lat, lon }) => {
        const params = new URLSearchParams({
          latitude: String(lat),
          longitude: String(lon),
          current:
            "temperature_2m,apparent_temperature,relative_humidity_2m,precipitation,weather_code,wind_speed_10m,wind_gusts_10m",
          hourly:
            "temperature_2m,precipitation_probability,weather_code,wind_speed_10m,wind_gusts_10m,cape,lightning_potential",
          daily: "weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max",
          temperature_unit: "fahrenheit",
          wind_speed_unit: "mph",
          precipitation_unit: "inch",
          timezone: "auto",
          past_days: "1",
          forecast_days: "16",
        });
        return `https://api.open-meteo.com/v1/forecast?${params.toString()}`;
      },
    }),
    // NWS only covers the US and may 404/400 for foreign or off-grid points;
    // callers treat any error as "no active alerts".
    getAlerts: builder.query<NwsAlerts, { lat: number; lon: number }>({
      query: ({ lat, lon }) => `https://api.weather.gov/alerts/active?point=${lat},${lon}&status=actual`,
    }),
  }),
});

export const { useGetForecastQuery, useGetAlertsQuery } = WeatherAPI;
